import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import * as Styled from './ProductFullDetailsModal.styled';
import OverlayModal from '../../../ContentSystem/components/OverlayModal/OverlayModal';
import SlidingPanelDrawer from '../../../SlidingPanelDrawer/SlidingPanelDrawer';
import SlidingPanel from '../../../SlidingPanel/SlidingPanel';
import PanelItem from '../../../SlidingPanel/PanelItem';
import ProductFullDetailsBody from '../ProductFullDetailsBody/ProductFullDetailsBody';
import { useBreakpoint } from '../../../../util/Breakpoint.context';
import { QuickViewContext } from '../../../QuickView/QuickView.context';
import ContainerQuery from '../../../ContainerQuery/ContainerQuery';
import { ProductFullDetailsModalFragment } from './ProductFullDetailsModal.gql';
import ErrorBoundary from '../../../ErrorBoundary/ErrorBoundary';
import { productAttributePropType } from '../../Product.propTypes';

/**
 * `ProductFullDetailsModal` displays all details about a product inside a modal.
 * On smaller viewports, the details are accessed through a drill-down menu.
 * On medium and larger viewports, the details are accessed through a sliding selector.
 */
const ProductFullDetailsModal = ({
  callouts,
  isOpen,
  onRequestClose,
  description_html,
  details
}) => {
  const currentBreakpoint = useBreakpoint();
  const [currentDetailsIndex, setCurrentDetailsIndex] = useState('0');
  const options = details.map((productType) => ({
    label: productType.title
  }));

  const { isQuickView } = useContext(QuickViewContext);

  if (!currentBreakpoint.medium && details.length > 1) {
    return (
      <SlidingPanelDrawer isOpen={isOpen} onEveryClose={onRequestClose}>
        <SlidingPanel panelId="details" title="Product Details">
          {details.map((productType, index) => (
            <PanelItem key={index} forPanelId={productType.title}>
              <Styled.PanelLabel>{productType.title}</Styled.PanelLabel>
            </PanelItem>
          ))}
        </SlidingPanel>
        {details.map((productType, index) => (
          <SlidingPanel
            key={index}
            panelId={productType.title}
            title="Product Details"
          >
            <Styled.ProductFullDetailsBody
              description_html={description_html}
              callouts={callouts}
              {...productType}
            />
          </SlidingPanel>
        ))}
      </SlidingPanelDrawer>
    );
  }

  return (
    <ErrorBoundary>
      <OverlayModal
        header="Product Details"
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        modalType="peeking"
        slides={options}
        onSelectSlide={(e) => setCurrentDetailsIndex(Number(e.target.value))}
        selectedSlide={currentDetailsIndex}
        size={isQuickView ? 'mega' : 'regular'}
        height={isQuickView ? '85vh' : 'auto'}
        smallLayoutTopMargin={24}
        withPadding={false}
      >
        <ContainerQuery>
          <ProductFullDetailsBody
            description_html={description_html}
            callouts={callouts}
            {...details[currentDetailsIndex]}
          />
        </ContainerQuery>
      </OverlayModal>
    </ErrorBoundary>
  );
};

const omitProp = (key, { [key]: _, ...obj }) => obj;
ProductFullDetailsModal.propTypes = {
  /**
   * Attribute value drivers to emphasize certain aspects of the product
   */
  callouts: PropTypes.arrayOf(PropTypes.shape(productAttributePropType)),
  /**
   * Determines whether `ProductFullDetailsModal` is open or closed.
   */
  isOpen: PropTypes.bool,
  /**
   * A function that is called when `ProductFullDetailsModal` is requesting to close.
   */
  onRequestClose: PropTypes.func,
  /**
   * Product description
   */
  description_html: PropTypes.string.isRequired,
  /**
   * Product(s) details (tech, materials, & care instructions)
   */
  details: PropTypes.arrayOf(
    PropTypes.shape(
      omitProp('description_html', ProductFullDetailsBody.propTypes)
    )
  ).isRequired
};

ProductFullDetailsModal.defaultProps = {
  callouts: [],
  isOpen: false,
  onRequestClose: null
};

ProductFullDetailsModal.fragment = ProductFullDetailsModalFragment;

export default ProductFullDetailsModal;
